import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import { Typography } from "@progress/kendo-react-common";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import FormTextField from "../../components/formFields/FormTextField";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import ShadowCard from "../../components/common/ShadowCard";
import {
  createContainerImportDischarge,
  getContainerImportDischargeByID,
  updateContainerImportDischarge,
} from "./services/containerImportDischarge.services";
import { clearContainerImportDischargeDetails } from "./containerImportDischargeSlice";
import FormDatePicker from "../../components/formFields/FormDateField";
import { closeDialog } from "../../components/dialog/dialogSlice";
import { getAllActiveNewPort } from "../newport/services/newport.services";
import { getAllActiveAgent } from "../agent/services/agent.services";
import { getAllActiveLine } from "../line/services/line.services";
import { IAgent } from "../agent/agentModel";
import FormMultiSelectionFiled from "../../components/formFields/FormMultiSelectionFiled";
import moment from "moment";

const CreateContainerImportDischarge: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const shipping_guid = location.state?.shipping_guid;
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const loading = useAppSelector(
    (state) => state.containerImportDischarge.loading
  );
  const AgentList = useAppSelector((state) => state.agent.AgentList);

  const ContainerImportDischargeDetail = useAppSelector(
    (state) => state.containerImportDischarge.ContainerImportDischargeDetail
  );
  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    dispatch(getAllActiveNewPort());
    dispatch(getAllActiveAgent());
    dispatch(getAllActiveLine());
    return () => {
      dispatch(clearContainerImportDischargeDetails());
      dispatch(closeDialog());
    };
  }, []);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [ContainerImportDischargeDetail, shipping_guid]);

  useEffect(() => {
    if (shipping_guid) {
      dispatch(getContainerImportDischargeByID(shipping_guid));
    }
  }, [shipping_guid]);

  const handleSubmit = async (values: any) => {
    const formdata = {
      // form_type: "CONTAINER",
      eta: values?.eta ? moment(values?.eta).format("YYYY-MM-DD") : "",
      vessels_name: values?.vessels_name,
      agent_id: values?.agent_id.join(","),
      cargo: values?.cargo,
      vcn: values?.vcn,
    };
    if (shipping_guid) {
      try {
        const updatePayload = {
          shipping_guid: shipping_guid,
          ...formdata,
        };

        const response = await dispatch(
          updateContainerImportDischarge(updatePayload)
        );
        if (response?.meta?.requestStatus === "fulfilled") {
          navigate("/shippingmovements");
        }
      } catch (error) {
        console.error("Error in handleSubmit:", error);
        throw error;
      }
    } else {
      try {
        const response = await dispatch(
          createContainerImportDischarge(formdata)
        );
        if (response?.meta?.requestStatus === "fulfilled") {
          navigate("/shippingmovements");
        }
      } catch (error) {
        console.error("Error in handleSubmit:", error);
        throw error;
      }
    }
  };

  if (loading) return <LoadingPanel gridRef={gridRef} />;

  return (
    <>
      <Form
        key={formKey}
        onSubmit={handleSubmit}
        initialValues={ContainerImportDischargeDetail}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement>
            <ShadowCard style={{ padding: 12 }}>
              <GridLayout
                style={{ marginRight: 30 }}
                gap={{ rows: 10, cols: 10 }}
                cols={[
                  { width: "25%" },
                  { width: "25%" },
                  { width: "25%" },
                  { width: "25%" },
                ]}
              >
                <GridLayoutItem colSpan={4}>
                  <Typography.h4>
                    {shipping_guid
                      ? "Update Container Import Discharge"
                      : "Add Container Import Discharge"}
                  </Typography.h4>
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="eta"
                    label="ETA"
                    component={FormDatePicker}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="vessels_name"
                    label="Vessel's Name"
                    placeholder="Vessels Name"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="vcn"
                    label="VCN No"
                    placeholder="i.e.78945144"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="agent_id"
                    label="Agent"
                    placeholder="Select Agent"
                    component={FormMultiSelectionFiled}
                    options={AgentList?.map((agent: IAgent) => {
                      return {
                        value: agent.id,
                        label: agent.agent_name,
                      };
                    })}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem>
                  <Field
                    name="cargo"
                    label="Cargo"
                    placeholder="Cargo"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>

                <GridLayoutItem
                  colSpan={4}
                  style={{
                    textAlign: "end",
                    marginTop: "20px",
                    width: "100%",
                  }}
                >
                  <ButtonWithLoading
                    label={shipping_guid ? "Update" : "Save"}
                    type="submit"
                    disabled={!formRenderProps.allowSubmit || loading}
                    loading={loading}
                  />
                  <Button
                    type="button"
                    fillMode={"outline"}
                    themeColor={"primary"}
                    style={{ marginLeft: 4 }}
                    onClick={() => navigate("/shippingmovements")}
                  >
                    Cancel
                  </Button>
                </GridLayoutItem>
              </GridLayout>
            </ShadowCard>
          </FormElement>
        )}
      />
    </>
  );
};

export default CreateContainerImportDischarge;
